// Chi tiết tài liệu: tóm tắt, xem file, góp ý dự thảo, lịch sử & phiên bản, người xem; thao tác theo trạng thái + quyền
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Dropdown, Input, Select, Table, Tabs, Timeline, Tooltip, message } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Copy, Download, Eye, FileX2, Lock, MoreHorizontal, Pencil, Send, Undo2, X } from 'lucide-react';
import DocShell, { EmptyState } from 'pages/Documents/components/DocShell';
import FileViewer, { downloadDocFile } from 'pages/Documents/components/FileViewer';
import { InfoRow } from 'pages/Documents/components/QuickInfo';
import useDocActions from 'pages/Documents/components/useDocActions';
import useDocsStore from 'pages/Documents/store';
import { blockName, deptName, issuerName, type Doc, type DocFile } from 'pages/Documents/data';
import { canViewDoc, categoryPath, docState, fmtSize, recipientIds } from 'pages/Documents/logic';
import { FileTypeIcon, STATE_META, StatePill, docLink, fmtDate, fmtDateTime, fmtEffective, personName, shareUrl, useDocAuth, usePeople } from 'pages/Documents/utils';

const ACCESS_LABEL = { view: 'Xem', download: 'Tải xuống', print: 'In', copy_link: 'Sao chép link' } as const;

export default function DocumentDetail() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const id = params.get('id') || '';
  const tab = params.get('tab') || 'summary';
  const { me, viewer } = useDocAuth();
  const actions = useDocActions();
  const people = usePeople();
  const doc = useDocsStore(s => s.docs.find(d => d.id === id));
  const allDocs = useDocsStore(s => s.docs);
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const markViewed = useDocsStore(s => s.markViewed);
  const logAccess = useDocsStore(s => s.logAccess);
  const addComment = useDocsStore(s => s.addComment);
  const [comment, setComment] = useState('');
  const [viewFileId, setViewFileId] = useState<string>();
  const [seenFilter, setSeenFilter] = useState('');
  const viewedRef = useRef<string>();

  const allowed = !!doc && canViewDoc(doc, viewer, people);

  // Ghi nhận đã xem (1 lần mỗi lần mở trang)
  useEffect(() => {
    if (doc && allowed && !doc.deletedAt && viewedRef.current !== doc.id) {
      viewedRef.current = doc.id;
      markViewed(doc.id, me);
    }
  }, [doc, allowed, markViewed, me]);

  const versions = useMemo(() => {
    if (!doc) return [];
    const byId = new Map(allDocs.map(d => [d.id, d]));
    const chain: Doc[] = [doc];
    for (let cur = doc.replacesId ? byId.get(doc.replacesId) : undefined; cur && !chain.includes(cur); cur = cur.replacesId ? byId.get(cur.replacesId) : undefined) chain.push(cur);
    for (let cur = doc.replacedById ? byId.get(doc.replacedById) : undefined; cur && !chain.includes(cur); cur = cur.replacedById ? byId.get(cur.replacedById) : undefined) chain.unshift(cur);
    return chain;
  }, [doc, allDocs]);

  const setTab = (k: string) => {
    const next = new URLSearchParams(params);
    if (k === 'summary') next.delete('tab'); else next.set('tab', k);
    setParams(next, { replace: true });
  };

  const back = (
    <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/personal-documents'))} className="mb-3 flex items-center gap-2 text-sm text-slate-600 hover:text-[#357f96]">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#3e91ab] text-white"><ArrowLeft size={15} /></span> Quay lại
    </button>
  );

  if (!doc || (doc.deletedAt && !viewer.manage)) {
    return <DocShell title="Chi tiết tài liệu" breadcrumb={back}><EmptyState icon={<FileX2 />} title="Không tìm thấy tài liệu" desc="Tài liệu không tồn tại, đã bị xóa hoặc đường dẫn không đúng." action={<Button onClick={() => navigate('/personal-documents')}>Về tài liệu cá nhân</Button>} /></DocShell>;
  }
  if (!allowed) {
    return <DocShell title="Chi tiết tài liệu" breadcrumb={back}><EmptyState icon={<Lock />} title="Bạn không có quyền xem tài liệu này" desc="Tài liệu chưa được chia sẻ cho bạn. Liên hệ người tạo hoặc quản lý tài liệu để được cấp quyền." /></DocShell>;
  }

  const state = docState(doc);
  const recipients = recipientIds(doc, people);
  const isOwnerOrManager = doc.createdBy === me.id || viewer.manage || viewer.approve;
  const typeName = types.find(t => t.id === doc.typeId)?.name || '—';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl(doc.id));
      message.success('Đã sao chép link');
      logAccess(doc.id, me, 'copy_link');
    } catch {
      message.error('Trình duyệt chặn sao chép, hãy copy thủ công');
    }
  };
  const download = async (f: DocFile) => {
    if (await downloadDocFile(f)) logAccess(doc.id, me, 'download', f.name);
  };
  const sendComment = () => {
    const c = comment.trim();
    if (c.length < 2) return message.warning('Nội dung góp ý quá ngắn');
    addComment(doc.id, me, c);
    setComment('');
    message.success('Đã gửi góp ý');
  };

  // ---- Thanh thao tác
  const p = actions.perm;
  const more = [
    p.submit(doc) && { key: 'submit', label: 'Gửi duyệt', icon: <Send size={14} />, onClick: () => actions.submit(doc) },
    p.withdraw(doc) && { key: 'withdraw', label: 'Rút lại về dự thảo', icon: <Undo2 size={14} />, onClick: () => actions.withdraw(doc) },
    p.reissue(doc) && { key: 'reissue', label: 'Ban hành lại (phiên bản mới)', onClick: () => navigate(`/add-new-document?reissue=${doc.id}`) },
    p.revoke(doc) && { key: 'revoke', label: 'Thu hồi', danger: true, onClick: () => actions.revoke(doc) },
    p.delete(doc) && { key: 'delete', label: 'Xóa', danger: true, onClick: () => actions.remove(doc, () => navigate(-1)) },
  ].filter(Boolean) as { key: string; label: string; danger?: boolean; icon?: React.ReactNode; onClick: () => void }[];

  const toolbar = (
    <>
      <Button icon={<Copy size={14} />} onClick={copyLink}>Copy link</Button>
      {p.edit(doc) && <Button icon={<Pencil size={14} />} onClick={() => navigate(`/add-new-document?id=${doc.id}`)}>{doc.status === 'published' ? 'Sửa phân quyền' : 'Sửa'}</Button>}
      {p.approve(doc) && <Button danger icon={<X size={14} />} onClick={() => actions.reject(doc)}>Từ chối</Button>}
      {p.approve(doc) && <Button type="primary" icon={<Check size={14} />} onClick={() => actions.approve(doc)}>Duyệt & ban hành</Button>}
      {more.length > 0 && <Dropdown trigger={['click']} menu={{ items: more.map(m => ({ key: m.key, label: m.label, danger: m.danger, icon: m.icon, onClick: m.onClick })) }}><Button icon={<MoreHorizontal size={16} />} /></Dropdown>}
    </>
  );

  // ---- Thông báo theo trạng thái
  const replacedBy = doc.replacedById ? allDocs.find(d => d.id === doc.replacedById) : undefined;
  const banner = doc.deletedAt ? <Alert type="error" showIcon message={`Tài liệu đang trong thùng rác (xóa bởi ${doc.deletedBy || '—'} lúc ${fmtDateTime(doc.deletedAt)})`} />
    : state === 'revoked' ? <Alert type="error" showIcon message={`Tài liệu đã bị thu hồi ngày ${fmtDate(doc.revokedAt)}`} description={doc.revokeReason} />
    : state === 'replaced' ? <Alert type="warning" showIcon message="Tài liệu đã bị thay thế" description={replacedBy && <>Phiên bản mới: <Link to={docLink(replacedBy.id)} className="font-medium">{replacedBy.code} — {replacedBy.title}</Link></>} />
    : state === 'rejected' ? <Alert type="error" showIcon message="Tài liệu bị từ chối" description={doc.rejectReason} />
    : state === 'pending' ? <Alert type="warning" showIcon message={`Đang chờ duyệt từ ${fmtDateTime(doc.submittedAt)}`} />
    : state === 'draft' ? <Alert type="info" showIcon message={doc.draftMode ? 'Bản dự thảo đang mở góp ý — người được chia sẻ có thể xem và góp ý.' : 'Bản dự thảo riêng — chỉ người tạo và quản lý tài liệu xem được.'} />
    : state === 'upcoming' ? <Alert type="info" showIcon message={`Tài liệu có hiệu lực từ ngày ${fmtDate(doc.effectiveFrom)}`} />
    : state === 'expired' ? <Alert type="warning" showIcon message={`Tài liệu đã hết hiệu lực từ ngày ${fmtDate(doc.effectiveTo)}`} />
    : null;

  const summary = (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div className="xl:col-span-2">
        <InfoRow label="Tên tài liệu">{doc.title}</InfoRow>
        <InfoRow label="Tình trạng"><StatePill doc={doc} /></InfoRow>
        <InfoRow label="Mã tài liệu">{doc.code}</InfoRow>
        <InfoRow label="Số tài liệu">{doc.number || '—'}</InfoRow>
        <InfoRow label="Loại / Danh mục">{typeName} · {categoryPath(categories, doc.categoryId)}</InfoRow>
        <InfoRow label="Hiệu lực">{fmtEffective(doc)}</InfoRow>
        <InfoRow label="Đơn vị ban hành">{deptName(doc.unitId)}</InfoRow>
        <InfoRow label="Khối/nghiệp vụ">{blockName(doc.blockId)}</InfoRow>
        <InfoRow label="Chủ thể ban hành">{issuerName(doc.issuerId)}</InfoRow>
        <InfoRow label="Người ký">{personName(people, doc.signerId)}</InfoRow>
        <InfoRow label="Người tạo">{doc.createdByName} · {fmtDateTime(doc.createdAt)}</InfoRow>
        {doc.approvedAt && <InfoRow label="Người duyệt">{doc.approvedBy} · {fmtDateTime(doc.approvedAt)}</InfoRow>}
        <InfoRow label="Link chia sẻ">
          <div className="flex flex-wrap items-center gap-2">
            <span className="max-w-full truncate text-[#357f96]">{shareUrl(doc.id)}</span>
            <Button size="small" icon={<Copy size={12} />} onClick={copyLink}>Copy link</Button>
          </div>
        </InfoRow>
        {doc.summary && <div className="mt-3 whitespace-pre-line rounded-lg bg-slate-50 p-3 text-sm text-slate-600">{doc.summary}</div>}

        <div className="mb-2 mt-6 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">File đính kèm ({doc.files.length})</h3>
          {p.editContent(doc) && <Button size="small" type="link" onClick={() => navigate(`/add-new-document?id=${doc.id}`)}>+ Thêm file</Button>}
        </div>
        <Table<DocFile>
          size="small" rowKey="id" dataSource={doc.files} pagination={false} scroll={{ x: 480 }}
          columns={[
            { title: 'File', key: 'name', render: (_v, f) => <button onClick={() => { setViewFileId(f.id); setTab('view'); }} className="flex items-center gap-2 text-left font-medium text-slate-700 hover:text-[#357f96]"><FileTypeIcon ext={f.ext} /><span className="truncate">{f.name}</span></button> },
            { title: 'Kích thước', dataIndex: 'size', width: 100, render: v => fmtSize(v) },
            { title: 'Loại file', dataIndex: 'ext', width: 80, render: v => String(v).toUpperCase() },
            {
              title: '', key: 'act', width: 80, align: 'right',
              render: (_v, f) => (
                <div className="flex justify-end gap-3 text-slate-400">
                  <Tooltip title="Xem"><Eye size={15} className="cursor-pointer hover:text-[#357f96]" onClick={() => { setViewFileId(f.id); setTab('view'); }} /></Tooltip>
                  <Tooltip title="Tải xuống"><Download size={15} className="cursor-pointer hover:text-[#357f96]" onClick={() => download(f)} /></Tooltip>
                </div>
              ),
            },
          ]}
        />
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-800">Phiên bản</div>
          {versions.length > 1 ? (
            <ul className="space-y-2">
              {versions.map(v => (
                <li key={v.id} className={`rounded-md px-2 py-1.5 text-sm ${v.id === doc.id ? 'bg-[#ecf6f9]' : ''}`}>
                  {v.id === doc.id ? <span className="font-semibold text-[#2c6b7f]">{v.code}</span> : <Link to={docLink(v.id)} className="font-medium text-[#357f96] hover:underline">{v.code}</Link>}
                  <div className="text-xs text-slate-500">{fmtEffective(v)} · {STATE_META[docState(v)].label}</div>
                </li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-400">Chưa có phiên bản khác</div>}
        </div>
        <div className="rounded-lg border border-slate-200 p-4">
          <div className="mb-2 text-sm font-bold text-slate-800">Chia sẻ</div>
          <div className="text-sm text-slate-600">
            {doc.shareAll ? 'Toàn công ty' : `${recipients.length} người`}
            {!doc.shareAll && doc.internalShare && <span className="text-slate-400"> (gồm chia sẻ nội bộ {deptName(people.find(x => x.id === doc.createdBy)?.deptId || '')})</span>}
          </div>
          {(doc.status === 'published' || doc.status === 'revoked') && (
            <div className="mt-1 text-sm text-slate-600">Đã xem: <b>{recipients.filter(r => doc.views[r]).length}</b>/{recipients.length}</div>
          )}
          <div className="mt-1 text-xs text-slate-400">{doc.emailNotify ? 'Có gửi email thông báo khi ban hành' : 'Không gửi email thông báo'}</div>
        </div>
      </div>
    </div>
  );

  const comments = (
    <div className="max-w-3xl">
      {doc.comments.length === 0 && <div className="py-6 text-sm text-slate-400">Chưa có góp ý.</div>}
      <ul className="space-y-3">
        {doc.comments.map(c => (
          <li key={c.id} className="flex gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ecf6f9] text-xs font-bold text-[#2c6b7f]">{c.userName.split(' ').pop()?.[0]}</div>
            <div className="min-w-0 flex-1 rounded-lg bg-slate-50 p-3">
              <div className="text-xs text-slate-500"><b className="text-slate-700">{c.userName}</b> · {fmtDateTime(c.at)}</div>
              <div className="mt-1 whitespace-pre-line text-sm text-slate-700">{c.content}</div>
            </div>
          </li>
        ))}
      </ul>
      {p.comment(doc) ? (
        <div className="mt-4">
          <Input.TextArea rows={3} maxLength={1000} showCount value={comment} onChange={e => setComment(e.target.value)} placeholder="Nhập góp ý cho bản dự thảo…"
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) sendComment(); }} />
          <div className="mt-2 flex items-center justify-between"><span className="text-xs text-slate-400">Ctrl + Enter để gửi</span><Button type="primary" icon={<Send size={14} />} onClick={sendComment}>Gửi góp ý</Button></div>
        </div>
      ) : <div className="mt-4 text-xs text-slate-400">Chỉ góp ý được khi tài liệu là dự thảo đang mở góp ý.</div>}
    </div>
  );

  const history = (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div>
        <div className="mb-3 text-sm font-bold text-slate-800">Lịch sử xử lý</div>
        <Timeline items={[...doc.history].reverse().map(h => ({
          color: /Từ chối|Thu hồi|thùng rác/.test(h.action) ? 'red' : /ban hành/i.test(h.action) ? 'green' : 'blue',
          children: <div><div className="text-sm font-medium text-slate-700">{h.action}</div><div className="text-xs text-slate-400">{h.by} · {fmtDateTime(h.at)}</div>{h.note && <div className="mt-1 text-xs text-slate-600">“{h.note}”</div>}</div>,
        }))} />
      </div>
      {isOwnerOrManager && (
        <div>
          <div className="mb-3 text-sm font-bold text-slate-800">Nhật ký truy cập</div>
          <Table
            size="small" rowKey={(_r, i) => String(i)} dataSource={[...doc.access].reverse()} pagination={{ pageSize: 10, size: 'small' }}
            columns={[
              { title: 'Người', dataIndex: 'userName' },
              { title: 'Thao tác', dataIndex: 'action', width: 120, render: (v: keyof typeof ACCESS_LABEL, r) => <span>{ACCESS_LABEL[v]}{r.fileName ? <span className="block truncate text-[11px] text-slate-400">{r.fileName}</span> : null}</span> },
              { title: 'Thời gian', dataIndex: 'at', width: 140, render: v => fmtDateTime(v) },
            ]}
          />
        </div>
      )}
    </div>
  );

  const viewersRows = recipients.map(rid => ({ id: rid, person: people.find(x => x.id === rid)!, at: doc.views[rid] }))
    .filter(r => r.person && (!seenFilter || (seenFilter === 'seen' ? !!r.at : !r.at)));
  const viewersTab = (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Select value={seenFilter} onChange={setSeenFilter} className="w-40" options={[{ value: '', label: 'Tất cả' }, { value: 'seen', label: 'Đã xem' }, { value: 'unseen', label: 'Chưa xem' }]} />
        <span className="text-sm text-slate-500">Đã xem <b>{recipients.filter(r => doc.views[r]).length}</b> / {recipients.length} người được chia sẻ</span>
      </div>
      <Table
        size="small" rowKey="id" dataSource={viewersRows} scroll={{ x: 600 }}
        pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: t => `${t} người` }}
        columns={[
          { title: 'Họ và tên', key: 'name', render: (_v, r) => <span className="font-medium text-slate-700">{r.person.name}</span> },
          { title: 'Phòng ban', key: 'dept', render: (_v, r) => deptName(r.person.deptId) },
          { title: 'Chức vụ', key: 'title', render: (_v, r) => r.person.title },
          { title: 'Tình trạng', key: 'seen', width: 180, render: (_v, r) => (r.at ? <span className="text-green-600">Đã xem · {fmtDateTime(r.at)}</span> : <span className="font-medium text-red-500">Chưa xem</span>) },
        ]}
      />
    </div>
  );

  const showComments = doc.status === 'draft' || doc.comments.length > 0;
  const tabs = [
    { key: 'summary', label: 'Tóm tắt tài liệu', children: summary },
    {
      key: 'view', label: `Xem tài liệu (${doc.files.length})`,
      children: <FileViewer files={doc.files} fileId={viewFileId} onFileChange={setViewFileId}
        placeholder={{ title: doc.title, code: doc.code, summary: doc.summary, unit: deptName(doc.unitId) }}
        onAccess={(a, f) => logAccess(doc.id, me, a, f.name)} />,
    },
    ...(showComments ? [{ key: 'comments', label: `Góp ý (${doc.comments.length})`, children: comments }] : []),
    { key: 'history', label: 'Lịch sử', children: history },
    ...(isOwnerOrManager && recipients.length ? [{ key: 'viewers', label: 'Người xem', children: viewersTab }] : []),
  ];

  return (
    <DocShell
      breadcrumb={back}
      title={<span className="leading-snug">{doc.title}</span>}
      subtitle={<span className="flex flex-wrap items-center gap-2">{doc.code}<span className="text-slate-300">·</span>{doc.approvedAt ? `Ban hành: ${fmtDate(doc.approvedAt)}` : `Tạo: ${fmtDate(doc.createdAt)}`}<StatePill doc={doc} /></span>}
      actions={!doc.deletedAt && toolbar}
    >
      {actions.modals}
      {banner && <div className="mb-3">{banner}</div>}
      <div className="rounded-lg border border-slate-200 bg-white px-4 pb-4">
        <Tabs activeKey={tabs.some(t => t.key === tab) ? tab : 'summary'} onChange={setTab} items={tabs} />
      </div>
    </DocShell>
  );
}
